import React, { useEffect, useState } from 'react';
import { useHybridAuth } from '../../hooks/useHybridAuth';
import { driverApi, DriverProfile, DriverCall } from '../../services/driverApi';
import { getDriverProfile } from '../../hooks/useDriverProfile';
import { isValidDriverRecordId } from '../../utils/driverRecordId';
import {
  AuthErrorKind,
  authErrorKindFromError,
} from '../../utils/authErrorKind';
import relayIcon from '../../assets/icons/relay-icon.png';

interface SplashScreenProps {
  onComplete: (
    dataLoaded: boolean,
    data?: { profile: DriverProfile; calls: DriverCall[] },
    error?: string,
    authError?: AuthErrorKind | null
  ) => void;
  duration?: number;
}

const SplashScreen: React.FC<SplashScreenProps> = ({
  onComplete,
  duration = 2000,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const { getAccessTokenSilently, isAuthenticated } = useHybridAuth();

  useEffect(() => {
    const loadAppData = async () => {
      if (!isAuthenticated) {
        // If not authenticated, wait for authentication first
        const timer = setTimeout(() => {
          setIsVisible(false);
          setTimeout(() => onComplete(false), 300);
        }, duration);
        return () => clearTimeout(timer);
      }

      try {
        setIsLoadingData(true);
        setDataError(null);

        // Get Auth0 token; getDriverProfile sets it in the API client and
        // picks a company first, which a fresh device has not stored yet.
        const token = await getAccessTokenSilently();
        const profileResponse = await getDriverProfile(token);

        // Placeholder profile (no Driver row) — skip the board: nothing can be assigned to it
        if (!isValidDriverRecordId(profileResponse.id)) {
          setDataError('Driver not found. Please contact your administrator');
          setTimeout(() => {
            setIsVisible(false);
            setTimeout(
              () =>
                onComplete(
                  false,
                  undefined,
                  'Driver not found. Please contact your administrator'
                ),
              300
            );
          }, 2000);
          return;
        }

        const callsResponse = await driverApi.getCalls();

        // Ensure minimum splash duration for UX
        const minDuration = Math.max(duration - 1000, 1000);
        setTimeout(() => {
          setIsVisible(false);
          setTimeout(
            () =>
              onComplete(true, {
                profile: profileResponse,
                calls: callsResponse.data,
              }),
            300
          );
        }, minDuration);
      } catch (error) {
        console.error('Failed to load driver data:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to load data';

        const authError = authErrorKindFromError(error);

        setDataError(errorMessage);

        // Still proceed after showing error briefly
        setTimeout(() => {
          setIsVisible(false);
          setTimeout(
            () => onComplete(false, undefined, errorMessage, authError),
            300
          );
        }, 2000);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadAppData();
  }, [onComplete, duration, isAuthenticated, getAccessTokenSilently]);

  if (!isVisible) {
    return (
      <div className="fixed inset-0 bg-primary flex flex-col items-center justify-center z-50 opacity-0 transition-opacity duration-300">
        {/* Fade out */}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-primary flex flex-col items-center justify-center z-50">
      {/* App Icon Container */}
      <div className="mb-8">
        <div className="w-20 h-20 flex items-center justify-center">
          <img
            src={relayIcon}
            alt="Relay Towing"
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      {/* App Name */}
      <div className="text-center mb-8">
        <h1 className="text-white text-xl font-medium tracking-wide font-schibsted">
          Relay Tow
        </h1>
      </div>

      {/* Loading indicator */}
      {isAuthenticated && (
        <div className="text-center">
          {isLoadingData && (
            <div className="flex items-center justify-center gap-2 text-white">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span className="text-sm opacity-80">Loading your data...</span>
            </div>
          )}
          {dataError && (
            <div className="text-white text-sm">
              <p>Loading...</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SplashScreen;
